import axios from 'axios'
import { authStorage } from '../storage/authStorage'

const legacyLoginClient = axios.create()
let restorePromise = null

export const ensureLoginToken = () => {
    const currentToken = authStorage.getToken()
    if (currentToken) return Promise.resolve(currentToken)

    const member = authStorage.getMember()
    if (!member?.id || member.temp === '1') {
        return Promise.resolve(null)
    }

    if (!restorePromise) {
        // 优先用服务端有效会话补发凭证；旧版缓存仍有账号密码时再走兼容登录。
        restorePromise = legacyLoginClient.post('/api/member/refreshToken', null, {
            withCredentials: true,
        }).then(response => {
            if (response.data?.code !== 200 ||
                String(response.data?.data?.memberId) !== String(member.id)) return null
            return response.data?.data?.token || null
        }).catch(() => null).then(async sessionToken => {
            if (sessionToken || !member.mobile || !member.password) return sessionToken
            const response = await legacyLoginClient.post('/api/member/login', {
                phone: member.mobile,
                password: member.password,
            })
            const token = response.headers['x-auth-token'] || response.data?.data?.loginToken
            if (response.data?.code !== 200 ||
                String(response.data?.data?.id) !== String(member.id)) return null
            return token || null
        }).then(token => {
            const activeMember = authStorage.getMember()
            if (!token || String(activeMember?.id) !== String(member.id)) return null
            authStorage.setToken(token)
            // 升级完成后不再把旧版遗留的明文密码留在浏览器中。
            const { password, loginToken, ...safeMember } = activeMember
            authStorage.setMember(safeMember)
            return token
        }).catch(() => null).finally(() => {
            restorePromise = null
        })
    }
    return restorePromise
}

// 服务端撤销或丢失旧凭证时，仅对本系统接口补发并重试一次。
export const retryWithFreshLoginToken = async (client, error) => {
    const request = error?.config
    const url = request?.url || ''
    if (error?.response?.status !== 401 || request?._authRetry ||
        !url.startsWith('/api/') || /\/member\/(login|logout|refreshToken)(?:\?|$)/.test(url)) {
        return Promise.reject(error)
    }
    const sentToken = request.headers?.get?.('X-Auth-Token') || request.headers?.['X-Auth-Token']
    if (sentToken && authStorage.getToken() === sentToken) authStorage.clearToken()
    const freshToken = await ensureLoginToken()
    if (!freshToken || freshToken === sentToken) return Promise.reject(error)
    request._authRetry = true
    request.headers = request.headers || {}
    request.headers['X-Auth-Token'] = freshToken
    return client(request)
}

// 旧页面仍直接调用 axios，统一补上登录凭证，避免页面之间出现认证行为差异。
axios.interceptors.request.use(async (config) => {
    const isAuthEndpoint = /\/member\/(login|logout|refreshToken)(?:\?|$)/.test(config.url || '')
    const token = isAuthEndpoint ? authStorage.getToken() : await ensureLoginToken()
    if (token) {
        config.headers = config.headers || {}
        config.headers['X-Auth-Token'] = token
    }
    return config
})

axios.interceptors.response.use(response => response, error => retryWithFreshLoginToken(axios, error))
